// VGM player — walks a parsed VgmFile's command stream, applying register
// writes and timed waits to a ChipSink. Pure and chip-agnostic: it knows
// nothing about how the sink turns register writes into PCM, only the
// (writeRegister, advance) protocol. The sink is a real core's APU in
// production (./vgm-synth.ts) and a recording mock in tests.
//
// Timing model: VGM waits are in samples at 44.1 kHz. render(budget)
// produces up to `budget` samples of output, calling sink.advance() for
// the elapsed waits and sink.writeRegister() at exact sample boundaries
// between them. The sink accumulates the audio; the player only sequences.
export class VgmPlayer {
    file;
    sink;
    loop;
    supported;
    // Program counter into file.commands, and the wait debt not yet paid
    // out as advanced samples.
    pc = 0;
    pendingWait = 0;
    done = false;
    constructor(file, sink, opts = {}) {
        this.file = file;
        this.sink = sink;
        this.loop = opts.loop ?? true;
        this.supported = new Set(sink.chips);
    }
    // True once a one-shot stream has hit its end marker (a looping stream
    // never finishes on its own).
    get finished() {
        return this.done;
    }
    // Whether any chip this file targets can actually be played by the sink.
    // A mismatch (e.g. a GB VGM on an NES synth) plays as pure silence; the
    // host surfaces this as a diagnostic rather than an error.
    get playable() {
        return this.file.chips.some(c => this.supported.has(c.chip));
    }
    reset() {
        this.pc = 0;
        this.pendingWait = 0;
        this.done = false;
    }
    // Produce up to `budget` output samples. Returns false once finished
    // (a one-shot reached its end). Safe to keep calling after that — it
    // advances the sink with silence so a mix stays time-aligned.
    render(budget) {
        let produced = 0;
        while (produced < budget) {
            if (this.done) {
                // Pad the remainder with silence so callers can rely on `budget`
                // samples having elapsed on the sink's clock.
                this.sink.advance(budget - produced);
                return false;
            }
            if (this.pendingWait > 0) {
                const step = Math.min(this.pendingWait, budget - produced);
                this.sink.advance(step);
                this.pendingWait -= step;
                produced += step;
                continue;
            }
            this.step();
        }
        return !this.done;
    }
    // Consume one command (no sample production — that happens via the
    // pendingWait/advance path).
    step() {
        const cmd = this.file.commands[this.pc];
        if (cmd === undefined) {
            // Ran off the end without an explicit 0x66 — treat as end.
            this.endOrLoop();
            return;
        }
        switch (cmd.op) {
            case "write":
                if (this.supported.has(cmd.chip)) {
                    this.sink.writeRegister(cmd.chip, cmd.reg, cmd.value);
                }
                this.pc++;
                break;
            case "wait":
                this.pendingWait += cmd.samples;
                this.pc++;
                break;
            case "end":
                this.endOrLoop();
                break;
        }
    }
    endOrLoop() {
        if (this.loop && this.file.loopCommandIndex !== null) {
            this.pc = this.file.loopCommandIndex;
        }
        else {
            this.done = true;
        }
    }
}
//# sourceMappingURL=vgm-player.js.map